import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import * as amplitude from '@amplitude/analytics-browser';
import { jwtDecode } from 'jwt-decode';

import {
  ServiceThemeOptions,
  defaultTheme,
  getThemeByServiceId,
} from '../themes';
import { LinkToken } from '../types/LinkToken';
import { tokenStorage } from '../utils/tokenStorage';
import { normalizeRedirectUrl } from '../utils/urlUtils';

// JWT State Interface
export interface JWTState {
  // Token data
  rawToken: string | null;
  decodedPayload: LinkToken | null;

  // Validity flags
  exists: boolean;
  isValid: boolean;
  isExpired: boolean;
  isDecodable: boolean;

  // Extracted data (stable references)
  serviceId: number | null;
  serviceName: string | null;
  fleetCode: string | null;
  redirectURISuccess: string | null;
  redirectURIError: string | null;
  theme: ServiceThemeOptions;

  // Error info
  errorType:
    | 'missing'
    | 'malformed'
    | 'expired'
    | 'invalid_signature'
    | 'unknown'
    | null;
  errorMessage: string | null;

  // Computed properties (for analytics compatibility)
  canRedirectToService: boolean;
  preferredRedirectURI: string | null;
}

// JWT Actions Interface
export interface JWTActions {
  setToken: (token: string) => void;
  clearToken: () => void;
  getAuthHeader: () => string;
  setupAnalytics: () => void;
  setServiceName: (name: string) => void;
}

// Combined Context Interface
export interface JWTContextValue extends JWTState, JWTActions {}

// Create Context
const JWTContext = createContext<JWTContextValue | null>(null);

// Helper function to validate token format
const isTokenValid = (token: string | null): boolean => {
  return token !== null && token.trim() !== '' && token.length > 20;
};

// Helper function to decode and analyze JWT
const analyzeJWT = (token: string | null): JWTState => {
  const baseState: JWTState = {
    rawToken: token,
    decodedPayload: null,
    exists: !!token,
    isValid: false,
    isExpired: false,
    isDecodable: false,
    serviceId: null,
    serviceName: null,
    fleetCode: null,
    redirectURISuccess: null,
    redirectURIError: null,
    theme: defaultTheme,
    errorType: null,
    errorMessage: null,
    canRedirectToService: false,
    preferredRedirectURI: null,
  };

  if (!token || !isTokenValid(token)) {
    return {
      ...baseState,
      errorType: 'missing',
      errorMessage: 'No valid JWT token provided',
    };
  }

  try {
    const decoded = jwtDecode<LinkToken>(token);

    const state: JWTState = {
      ...baseState,
      decodedPayload: decoded,
      isDecodable: true,
    };

    // Extract service information
    if (decoded.sid && typeof decoded.sid === 'number') {
      state.serviceId = decoded.sid;
      state.theme = getThemeByServiceId(decoded.sid);
    }

    if (decoded.fc) {
      state.fleetCode = decoded.fc;
    }

    if (decoded.urisuccess) {
      state.redirectURISuccess = normalizeRedirectUrl(decoded.urisuccess);
    }

    if (decoded.urierror) {
      state.redirectURIError = normalizeRedirectUrl(decoded.urierror);
    }

    // Check expiration
    if (decoded.exp && typeof decoded.exp === 'number') {
      const now = Math.floor(Date.now() / 1000);
      if (decoded.exp < now) {
        state.isExpired = true;
        state.errorType = 'expired';
        state.errorMessage = `Token expired at ${new Date(
          decoded.exp * 1000
        ).toISOString()}`;
      } else {
        state.isValid = true;
      }
    } else {
      // No expiration field - treat as valid
      state.isValid = true;
    }

    // Compute redirect capabilities
    state.canRedirectToService =
      state.isDecodable &&
      (!!state.serviceId || !!state.fleetCode) &&
      !!(state.redirectURIError || state.redirectURISuccess);

    // Preferred redirect URI (success first, then error)
    state.preferredRedirectURI =
      state.redirectURISuccess || state.redirectURIError || null;

    return state;
  } catch (error) {
    return {
      ...baseState,
      errorType: 'malformed',
      errorMessage:
        error instanceof Error ? error.message : 'Unknown token decoding error',
    };
  }
};

// JWT Provider Component
export const JWTProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();

  // Single JWT state - analyzed once and persisted for the session
  const [jwtState, setJWTState] = useState<JWTState>(() => {
    // Initial JWT resolution priority:
    // 1. URL token (if present) - for fresh links
    // 2. localStorage token - for returning users
    const urlParams = new URLSearchParams(location.search);
    const urlToken = urlParams.get('token');
    const storedToken = tokenStorage.get();

    const initialToken = urlToken || storedToken;

    // Analyze the initial token once
    const initialState = analyzeJWT(initialToken);

    // Load persisted service name immediately during initialization
    const persistedServiceName = localStorage.getItem('catena_service_name');
    if (persistedServiceName) {
      initialState.serviceName = persistedServiceName;
    }

    return initialState;
  });

  // Store valid tokens to localStorage for session persistence
  useEffect(() => {
    if (jwtState.isValid && jwtState.rawToken) {
      const currentStored = tokenStorage.get();
      if (currentStored !== jwtState.rawToken) {
        tokenStorage.set(jwtState.rawToken);
      }
    }
  }, [jwtState.isValid, jwtState.rawToken]);

  // Actions
  const setToken = useCallback((newToken: string) => {
    const newState = analyzeJWT(newToken);
    setJWTState(newState);

    if (newState.isValid) {
      tokenStorage.set(newToken);
    }
  }, []);

  const clearToken = useCallback(() => {
    setJWTState(analyzeJWT(null));
    tokenStorage.remove();
  }, []);

  const getAuthHeader = useCallback(() => {
    return jwtState.rawToken ? `Bearer ${jwtState.rawToken}` : '';
  }, [jwtState.rawToken]);

  const setupAnalytics = useCallback(() => {
    if (jwtState.isDecodable && jwtState.decodedPayload) {
      const { sid, fc } = jwtState.decodedPayload;

      if (sid) {
        amplitude.setUserId(`service-${sid}`);
      }

      if (fc) {
        amplitude.setDeviceId(`fleet-code-${fc}`);
      }
    }
  }, [jwtState.isDecodable, jwtState.decodedPayload]);

  // Auto-setup analytics when token becomes valid
  useEffect(() => {
    if (jwtState.isValid && jwtState.serviceId) {
      setupAnalytics();
    }
  }, [jwtState.isValid, jwtState.serviceId, setupAnalytics]);

  // Add setServiceName action
  const setServiceName = useCallback((name: string) => {
    setJWTState((prev) => ({
      ...prev,
      serviceName: name,
    }));
    // Store service name in localStorage for persistence
    localStorage.setItem('catena_service_name', name);
  }, []);

  // Context value - stable reference with current JWT state
  const contextValue: JWTContextValue = useMemo(
    () => ({
      ...jwtState,
      setToken,
      clearToken,
      getAuthHeader,
      setupAnalytics,
      setServiceName,
    }),
    [
      jwtState,
      setToken,
      clearToken,
      getAuthHeader,
      setupAnalytics,
      setServiceName,
    ]
  );

  return (
    <JWTContext.Provider value={contextValue}>{children}</JWTContext.Provider>
  );
};

// Hook to use JWT context
export const useJWT = (): JWTContextValue => {
  const context = useContext(JWTContext);
  if (!context) {
    throw new Error('useJWT must be used within a JWTProvider');
  }
  return context;
};

// Convenience hooks for specific JWT properties (to minimize re-renders)
export const useJWTToken = () => {
  const { rawToken } = useJWT();
  return rawToken;
};

export const useJWTValidity = () => {
  const { isValid, isExpired, errorType } = useJWT();
  return { isValid, isExpired, errorType };
};

export const useJWTServiceInfo = () => {
  const { serviceId, fleetCode, theme } = useJWT();
  return { serviceId, fleetCode, theme };
};

export const useJWTRedirects = () => {
  const { redirectURISuccess, redirectURIError } = useJWT();
  return { redirectURISuccess, redirectURIError };
};
